import { Items } from "./Items"
import { Map } from "./Map"

/**
 * Maps both keys and values of a record to create a new record.
 * Applies transformation functions to each key-value pair.
 *
 * @param input - Source record object
 * @param keyFn - Function to transform keys (receives value and key)
 * @param valueFn - Function to transform values (receives value and key)
 * @returns New record with transformed keys and values
 *
 * @example
 * ```ts
 * import { RecordMap } from "@pdfking/tfunc"
 *
 * const input = { a: 1, b: 2, c: 3 }
 * const result = RecordMap(
 *   input,
 *   (v, k) => k.toUpperCase(),
 *   (v, k) => v * 10
 * )
 * // { A: 10, B: 20, C: 30 }
 * ```
 */
export const RecordMap = <V, NV>(
  input: Record<string, V>,
  keyFn: (value: V, key: string) => string,
  valueFn: (value: V, key: string) => NV
): Record<string, NV> =>
  Object.fromEntries(
    Map(([k, v]) => [keyFn(v as V, k), valueFn(v as V, k)], Items(input))
  ) as Record<string, NV>
